import React, { useState, useEffect } from 'react';
import API from '../api/api';
import Pagination from './Pagination';

const EMPTY_FORM = {
  invoice_number: '',
  invoice_date: '',
  due_date: '',
  payment_terms: 'NET30',
  freight_charges: '',
  accessorial_charges: '',
  fuel_surcharge: '',
  total_amount: '',
  currency: 'USD',
  notes: '',
};

function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [ediFilter, setEdiFilter] = useState('');
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Edit state
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  // Delete state
  const [deletingInvoice, setDeletingInvoice] = useState(null);
  const [deleteConfirming, setDeleteConfirming] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage] = useState(20);

  useEffect(() => { fetchInvoices(); }, [currentPage, statusFilter, ediFilter]); // eslint-disable-line

  useEffect(() => {
    let filtered = invoices;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(inv =>
        (inv.invoice_number || '').toLowerCase().includes(q) ||
        (inv.shipment_pro_number || '').toLowerCase().includes(q) ||
        (inv.shipment_po_number || '').toLowerCase().includes(q)
      );
    }
    setFilteredInvoices(filtered);
  }, [invoices, searchTerm]);

  // Auto-calc total when charge fields change
  useEffect(() => {
    const freight = parseFloat(editForm.freight_charges) || 0;
    const accessorial = parseFloat(editForm.accessorial_charges) || 0;
    const fuel = parseFloat(editForm.fuel_surcharge) || 0;
    setEditForm(f => ({ ...f, total_amount: (freight + accessorial + fuel).toFixed(2) }));
  }, [editForm.freight_charges, editForm.accessorial_charges, editForm.fuel_surcharge]); // eslint-disable-line

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      if (statusFilter) params.append('status', statusFilter);
      if (ediFilter) params.append('edi_sent', ediFilter);

      const response = await API.get(`invoices/?${params.toString()}`);
      if (response.data.results) {
        setInvoices(response.data.results);
        setFilteredInvoices(response.data.results);
        setTotalCount(response.data.count || 0);
        setTotalPages(Math.ceil((response.data.count || 0) / itemsPerPage));
      } else {
        const list = Array.isArray(response.data) ? response.data : [];
        setInvoices(list);
        setFilteredInvoices(list);
        setTotalCount(list.length);
        setTotalPages(1);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError('Failed to load invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const formatDate = (dateString) =>
    dateString ? new Date(dateString).toLocaleDateString() : 'N/A';

  // ── Status inline change ──────────────────────────────────────
  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      setUpdatingStatus(invoiceId);
      setError(null);
      await API.patch(`invoices/${invoiceId}/`, { status: newStatus });
      showSuccess('Invoice status updated successfully!');
      await fetchInvoices();
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update invoice status. Please try again.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  // ── Edit modal ────────────────────────────────────────────────
  const handleEditClick = (invoice) => {
    setEditingInvoice(invoice);
    setEditForm({
      invoice_number: invoice.invoice_number || '',
      invoice_date: invoice.invoice_date || '',
      due_date: invoice.due_date || '',
      payment_terms: invoice.payment_terms || 'NET30',
      freight_charges: invoice.freight_charges ?? '',
      accessorial_charges: invoice.accessorial_charges ?? '',
      fuel_surcharge: invoice.fuel_surcharge ?? '',
      total_amount: invoice.total_amount ?? '',
      currency: invoice.currency || 'USD',
      notes: invoice.notes || '',
    });
    setEditError(null);
  };

  const handleEditSave = async () => {
    try {
      setEditSaving(true);
      setEditError(null);
      await API.patch(`invoices/${editingInvoice.id}/`, editForm);
      showSuccess('Invoice updated successfully!');
      setEditingInvoice(null);
      await fetchInvoices();
    } catch (err) {
      const msg = err.response?.data
        ? Object.values(err.response.data).flat().join(' ')
        : 'Failed to save changes.';
      setEditError(msg);
    } finally {
      setEditSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────
  const handleDeleteClick = (invoice) => {
    setDeletingInvoice(invoice);
    setDeleteConfirming(false);
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleteConfirming(true);
      await API.delete(`invoices/${deletingInvoice.id}/`);
      showSuccess(`Invoice ${deletingInvoice.invoice_number} deleted.`);
      setDeletingInvoice(null);
      if (filteredInvoices.length === 1 && currentPage > 1) {
        setCurrentPage(p => p - 1);
      } else {
        await fetchInvoices();
      }
    } catch (err) {
      console.error('Error deleting invoice:', err);
      setError('Failed to delete invoice. Please try again.');
      setDeletingInvoice(null);
    } finally {
      setDeleteConfirming(false);
    }
  };

  // ── PDF export ────────────────────────────────────────────────
  const handleExportPdf = async (invoice) => {
    try {
      const response = await API.get(`invoices/${invoice.id}/export_pdf/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      setError('Failed to export PDF. Please try again.');
    }
  };

  const getEdiBadge = (ediSent) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      ediSent ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
    }`}>
      {ediSent ? 'EDI Sent' : 'No EDI'}
    </span>
  );

  const displayInvoices = filteredInvoices;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Invoices</h1>
          <p className="mt-2 text-sm text-gray-600">Manage and track invoices for your shipments</p>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Invoice #, PRO #, PO #"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">EDI Status</label>
              <select
                value={ediFilter}
                onChange={(e) => setEdiFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">All</option>
                <option value="true">EDI Sent</option>
                <option value="false">No EDI</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={fetchInvoices}
                className="w-full bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4 flex items-center">
            <svg className="h-5 w-5 text-green-400 mr-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-green-800">{successMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4 flex items-center justify-between">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-red-400 mr-3 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}

        {/* Invoices Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {displayInvoices.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No invoices found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || statusFilter || ediFilter ? 'Try adjusting your filters.' : 'Get started by creating an invoice for a shipment.'}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {displayInvoices.map((invoice) => (
                <li key={invoice.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <p className="text-sm font-semibold text-gray-900">{invoice.invoice_number}</p>
                        <p className="text-sm text-gray-500">PRO: {invoice.shipment_pro_number} | PO: {invoice.shipment_po_number}</p>
                        {getEdiBadge(invoice.edi_sent)}
                      </div>

                      <div className="mt-2 flex items-center gap-3 flex-wrap text-sm text-gray-500">
                        <span>Invoice Date: {formatDate(invoice.invoice_date)}</span>
                        <span>Due: {formatDate(invoice.due_date)}</span>
                        <span>Terms: {invoice.payment_terms}</span>
                        {invoice.currency && <span>Currency: {invoice.currency}</span>}
                      </div>

                      <div className="mt-1 flex items-center gap-4 flex-wrap text-sm text-gray-400">
                        {invoice.freight_charges > 0 && <span>Freight: {formatCurrency(invoice.freight_charges)}</span>}
                        {invoice.accessorial_charges > 0 && <span>Accessorial: {formatCurrency(invoice.accessorial_charges)}</span>}
                        {invoice.fuel_surcharge > 0 && <span>Fuel: {formatCurrency(invoice.fuel_surcharge)}</span>}
                      </div>

                      {invoice.notes && (
                        <p className="mt-1 text-sm text-gray-500 italic truncate max-w-lg">{invoice.notes}</p>
                      )}
                    </div>

                    {/* Right: total + status + actions */}
                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      <p className="text-lg font-bold text-gray-900">{formatCurrency(invoice.total_amount)}</p>

                      {/* Inline status dropdown */}
                      <div className="relative flex items-center gap-1.5">
                        <label className="text-xs text-gray-500">Status:</label>
                        <select
                          value={invoice.status}
                          onChange={(e) => handleStatusChange(invoice.id, e.target.value)}
                          disabled={updatingStatus === invoice.id}
                          className="text-xs px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 disabled:cursor-wait"
                        >
                          <option value="draft">Draft</option>
                          <option value="sent">Sent</option>
                          <option value="paid">Paid</option>
                          <option value="overdue">Overdue</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                        {updatingStatus === invoice.id && (
                          <div className="animate-spin h-3 w-3 border-2 border-primary-600 border-t-transparent rounded-full" />
                        )}
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center gap-1.5 mt-1">
                        {/* PDF */}
                        <button
                          onClick={() => handleExportPdf(invoice)}
                          title="Download PDF"
                          className="inline-flex items-center px-2 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <svg className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          PDF
                        </button>

                        {/* Edit */}
                        <button
                          onClick={() => handleEditClick(invoice)}
                          title="Edit invoice"
                          className="inline-flex items-center px-2 py-1 border border-primary-300 rounded text-xs font-medium text-primary-700 bg-white hover:bg-primary-50"
                        >
                          <svg className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit
                        </button>

                        {/* Delete */}
                        <button
                          onClick={() => handleDeleteClick(invoice)}
                          title="Delete invoice"
                          className="inline-flex items-center px-2 py-1 border border-red-300 rounded text-xs font-medium text-red-700 bg-white hover:bg-red-50"
                        >
                          <svg className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalCount}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />

        {/* Summary */}
        {displayInvoices.length > 0 && (
          <div className="mt-6 bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
                <p className="text-sm text-gray-500">Total Invoices</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(displayInvoices.reduce((s, inv) => s + parseFloat(inv.total_amount), 0))}
                </p>
                <p className="text-sm text-gray-500">Current Page Total</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-primary-600">
                  {displayInvoices.filter(inv => inv.status === 'paid').length}
                </p>
                <p className="text-sm text-gray-500">Paid (This Page)</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">
                  {displayInvoices.filter(inv => inv.status === 'overdue').length}
                </p>
                <p className="text-sm text-gray-500">Overdue (This Page)</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Edit Modal ──────────────────────────────────────────── */}
      {editingInvoice && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setEditingInvoice(null)} />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 text-left z-10">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-semibold text-gray-900">
                  Edit Invoice — {editingInvoice.invoice_number}
                </h3>
                <button onClick={() => setEditingInvoice(null)} className="text-gray-400 hover:text-gray-600">
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>

              {editError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">{editError}</div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
                  <input
                    type="text"
                    value={editForm.invoice_number}
                    onChange={(e) => setEditForm(f => ({ ...f, invoice_number: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <select
                    value={editForm.currency}
                    onChange={(e) => setEditForm(f => ({ ...f, currency: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="USD">USD</option>
                    <option value="CAD">CAD</option>
                    <option value="MXN">MXN</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
                  <input
                    type="date"
                    value={editForm.invoice_date}
                    onChange={(e) => setEditForm(f => ({ ...f, invoice_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={editForm.due_date}
                    onChange={(e) => setEditForm(f => ({ ...f, due_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
                  <select
                    value={editForm.payment_terms}
                    onChange={(e) => setEditForm(f => ({ ...f, payment_terms: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="NET15">NET15</option>
                    <option value="NET30">NET30</option>
                    <option value="NET60">NET60</option>
                    <option value="DUE_ON_RECEIPT">Due on Receipt</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Freight Charges</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.freight_charges}
                    onChange={(e) => setEditForm(f => ({ ...f, freight_charges: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Accessorial Charges</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.accessorial_charges}
                    onChange={(e) => setEditForm(f => ({ ...f, accessorial_charges: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fuel Surcharge</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.fuel_surcharge}
                    onChange={(e) => setEditForm(f => ({ ...f, fuel_surcharge: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Amount
                    <span className="ml-1 text-xs text-gray-400">(auto-calculated)</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.total_amount}
                    onChange={(e) => setEditForm(f => ({ ...f, total_amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    rows={3}
                    value={editForm.notes}
                    onChange={(e) => setEditForm(f => ({ ...f, notes: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setEditingInvoice(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSave}
                  disabled={editSaving}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editSaving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ───────────────────────────── */}
      {deletingInvoice && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setDeletingInvoice(null)} />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6 text-left z-10">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-red-100">
                  <svg className="h-5 w-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Delete Invoice</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Are you sure you want to delete invoice <strong>{deletingInvoice.invoice_number}</strong>?
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="mt-5 flex justify-end gap-3">
                <button
                  onClick={() => setDeletingInvoice(null)}
                  disabled={deleteConfirming}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deleteConfirming}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteConfirming ? 'Deleting…' : 'Delete Invoice'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Invoices;
